import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import React, { useEffect, useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { AdminUser } from '@/pages/admin/users/columns' // Assuming type export
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar' // Add missing component import
import { Calendar as CalendarIcon } from 'lucide-react' // Alias Lucide icon
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { UseMutationResult } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

type BanDurationType = 'permanent' | 'duration' | 'date'

interface BanUserPayload {
	userId: string
	banReason?: string
	banExpiresIn?: number
}

interface BanUserDialogProps {
	user: AdminUser | null
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	banUserMutation: UseMutationResult<unknown, Error, BanUserPayload, unknown>
}

export function BanUserDialog({ user, isOpen, onOpenChange, banUserMutation }: BanUserDialogProps) {
	const { t } = useTranslation()
	const [banReason, setBanReason] = useState<string>('')
	const [banDurationType, setBanDurationType] = useState<BanDurationType>('permanent')
	const [banDurationPreset, setBanDurationPreset] = useState<string>('')
	const [banDurationCustom, setBanDurationCustom] = useState<string>('')
	const [banExpiresAtDate, setBanExpiresAtDate] = useState<Date | undefined>(undefined)

	// Reset state when dialog closes or user changes
	useEffect(() => {
		if (!isOpen) {
			setBanReason('')
			setBanDurationType('permanent')
			setBanDurationPreset('')
			setBanDurationCustom('')
			setBanExpiresAtDate(undefined)
		}
	}, [isOpen])

	const confirmBan = () => {
		if (user) {
			const payload: BanUserPayload = {
				userId: user.id,
			}
			if (banReason.trim()) {
				payload.banReason = banReason.trim()
			}

			let durationSeconds: number | undefined

			if (banDurationType === 'duration') {
				if (banDurationPreset) {
					switch (banDurationPreset) {
						case '1h':
							durationSeconds = 3600
							break
						case '1d':
							durationSeconds = 86400
							break
						case '1w':
							durationSeconds = 604800
							break
						case '1m':
							durationSeconds = 2592000
							break
						case 'custom': {
							const customSec = Number.parseInt(banDurationCustom, 10)
							if (!Number.isNaN(customSec) && customSec > 0) {
								durationSeconds = customSec
							}
							break
						}
						default:
							break
					}
				} else {
					const customSec = Number.parseInt(banDurationCustom, 10)
					if (!Number.isNaN(customSec) && customSec > 0) {
						durationSeconds = customSec
					}
				}
			} else if (banDurationType === 'date' && banExpiresAtDate) {
				const now = new Date()
				const diffMillis = banExpiresAtDate.getTime() - now.getTime()
				if (diffMillis > 0) {
					durationSeconds = Math.round(diffMillis / 1000)
				} else {
					toast.error(t('toasts.invalidBanDate'))
					return
				}
			}
			if (durationSeconds !== undefined && durationSeconds > 0) {
				payload.banExpiresIn = durationSeconds
			} else if (banDurationType !== 'permanent') {
				toast.warning(t('toasts.invalidBanDuration'))
				return
			}

			banUserMutation.mutate(payload)
		}
	}

	return (
		<AlertDialog open={isOpen} onOpenChange={onOpenChange}>
			<AlertDialogContent className='sm:max-w-md'>
				<AlertDialogHeader>
					<AlertDialogTitle>
						{t('admin.users.page.banDialog.title', {
							userIdentifier: user?.email || user?.id,
						})}
					</AlertDialogTitle>
					<AlertDialogDescription>{t('admin.users.page.banDialog.description')}</AlertDialogDescription>
				</AlertDialogHeader>
				<div className='grid gap-4 py-4'>
					<div className='grid grid-cols-4 items-center gap-4'>
						<Label htmlFor='ban-reason' className='text-right'>
							{t('admin.users.page.banDialog.reasonLabel')}
						</Label>
						<Input
							id='ban-reason'
							value={banReason}
							onChange={(e) => setBanReason(e.target.value)}
							className='col-span-3'
							placeholder={t('admin.users.page.banDialog.reasonPlaceholder')}
						/>
					</div>

					<div className='grid grid-cols-4 items-start gap-4'>
						<Label className='text-right pt-2'>{t('admin.users.page.banDialog.durationLabel')}</Label>
						<RadioGroup
							value={banDurationType}
							onValueChange={(value: BanDurationType) => {
								setBanDurationType(value)
								setBanDurationPreset('')
								setBanDurationCustom('')
								setBanExpiresAtDate(undefined)
							}}
							className='col-span-3 space-y-2'
						>
							<div className='flex items-center space-x-2'>
								<RadioGroupItem value='permanent' id='r-permanent' />
								<Label htmlFor='r-permanent'>
									{t('admin.users.page.banDialog.durationTypePermanent')}
								</Label>
							</div>
							<div className='flex items-center space-x-2'>
								<RadioGroupItem value='duration' id='r-duration' />
								<Label htmlFor='r-duration'>
									{t('admin.users.page.banDialog.durationTypeDuration')}
								</Label>
							</div>
							<div className='flex items-center space-x-2'>
								<RadioGroupItem value='date' id='r-date' />
								<Label htmlFor='r-date'>{t('admin.users.page.banDialog.durationTypeDate')}</Label>
							</div>
						</RadioGroup>
					</div>

					{banDurationType === 'duration' && (
						<div className='grid grid-cols-4 items-center gap-4 pl-10'>
							<Label htmlFor='ban-duration-preset' className='text-right col-start-1'>
								{t('admin.users.page.banDialog.durationPresetLabel')}
							</Label>
							<div className='col-span-3 flex gap-2'>
								<Select
									value={banDurationPreset}
									onValueChange={(value) => {
										setBanDurationPreset(value)
										if (value !== 'custom') setBanDurationCustom('')
									}}
								>
									<SelectTrigger id='ban-duration-preset' className='flex-1'>
										<SelectValue
											placeholder={t('admin.users.page.banDialog.durationPresetPlaceholder')}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='1h'>
											{t('admin.users.page.banDialog.durationPreset1h')}
										</SelectItem>
										<SelectItem value='1d'>
											{t('admin.users.page.banDialog.durationPreset1d')}
										</SelectItem>
										<SelectItem value='1w'>
											{t('admin.users.page.banDialog.durationPreset1w')}
										</SelectItem>
										<SelectItem value='1m'>
											{t('admin.users.page.banDialog.durationPreset1m')}
										</SelectItem>
										<SelectItem value='custom'>
											{t('admin.users.page.banDialog.durationPresetCustom')}
										</SelectItem>
									</SelectContent>
								</Select>
								{banDurationPreset === 'custom' && (
									<Input
										id='ban-duration-custom'
										type='number'
										value={banDurationCustom}
										onChange={(e) => setBanDurationCustom(e.target.value)}
										className='flex-1'
										placeholder={t('admin.users.page.banDialog.durationCustomPlaceholder')}
										min='1'
									/>
								)}
							</div>
						</div>
					)}

					{banDurationType === 'date' && (
						<div className='grid grid-cols-4 items-center gap-4 pl-10'>
							<Label htmlFor='ban-expiry-date' className='text-right col-start-1'>
								{t('admin.users.page.banDialog.expiryDateLabel')}
							</Label>
							<Popover>
								<PopoverTrigger asChild>
									<Button
										variant={'outline'}
										className={cn(
											'col-span-3 justify-start text-left font-normal',
											!banExpiresAtDate && 'text-muted-foreground'
										)}
									>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{banExpiresAtDate ? (
											format(banExpiresAtDate, 'PPP')
										) : (
											<span>{t('admin.users.page.banDialog.expiryDatePlaceholder')}</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={banExpiresAtDate}
										onSelect={setBanExpiresAtDate}
										initialFocus
										disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
									/>
								</PopoverContent>
							</Popover>
						</div>
					)}
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
					<AlertDialogAction
						onClick={confirmBan}
						disabled={banUserMutation.isPending}
						className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
					>
						{banUserMutation.isPending
							? t('common.banning')
							: t('admin.users.page.banDialog.confirmButton')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
